import React, { useState } from 'react';

const MarketMoversPanel = ({ marketMovers, portfolioMovers }) => {
  const [hoveredStock, setHoveredStock] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [viewMode, setViewMode] = useState('market'); // 'market' or 'portfolio'

  const handleMouseEnter = (stock, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      x: rect.right + 10,
      y: rect.top
    });
    setHoveredStock(stock);
  };

  const handleMouseLeave = () => {
    setHoveredStock(null);
  };

  const formatNumber = (num) => {
    if (!num) return 'N/A';
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Use the appropriate data based on view mode
  const activeData = viewMode === 'market' ? marketMovers : portfolioMovers;
  const gainers = activeData?.gainers || [];
  const losers = activeData?.losers || [];
  
  // Loading state from backend
  const isLoading = viewMode === 'market' && marketMovers?.loading;
  const loadingProgress = marketMovers?.loadingProgress || 0;
  const totalAnalyzed = marketMovers?.totalStocksAnalyzed || 0;
  const totalTarget = marketMovers?.totalStocksTarget || 0;
  const rateLimited = marketMovers?.rateLimited;
  const rateLimitSecondsRemaining = marketMovers?.rateLimitSecondsRemaining || 0;
  const cacheAge = marketMovers?.cacheAge;
  
  // Calculate if scan is complete (95%+)
  const isComplete = totalTarget > 0 && totalAnalyzed >= (totalTarget * 0.95);
  
  // Estimate time to complete based on current progress
  const estimateTimeRemaining = () => {
    if (isComplete || totalTarget === 0) return null;
    const remaining = totalTarget - totalAnalyzed;
    // ~50 stocks per cycle, ~45 seconds per cycle (including rate limit cooldowns)
    const cyclesNeeded = Math.ceil(remaining / 50);
    const secondsRemaining = cyclesNeeded * 45;
    if (secondsRemaining < 60) return `~${secondsRemaining}s`;
    return `~${Math.ceil(secondsRemaining / 60)}m`;
  };

  return (
    <>
      <div className="absolute left-2 top-44 bottom-12 w-56 bg-slate-800/90 backdrop-blur-sm rounded-lg border border-slate-700 overflow-hidden flex flex-col max-sm:relative max-sm:inset-auto max-sm:w-full max-sm:mb-2 max-sm:h-[40vh]">
        {/* Header with Toggle */}
        <div className="px-2 py-1.5 bg-slate-900/50 border-b border-slate-700 shrink-0">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-[10px] font-bold text-slate-300 uppercase tracking-wide">Movers</h3>
            {/* Status Indicator - Always show for Market mode */}
            {viewMode === 'market' && (
              <div className="flex items-center gap-1">
                {isLoading && !rateLimited && (
                  <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" title={`Scanning ${totalAnalyzed}/${totalTarget}`}></div>
                )}
                {rateLimited && (
                  <div className="w-2 h-2 bg-orange-500 rounded-full" title="Rate limited - cooling down"></div>
                )}
                {isComplete && !isLoading && (
                  <div className="w-2 h-2 bg-emerald-400 rounded-full" title="Scan complete"></div>
                )}
              </div>
            )}
          </div>
          
          {/* Progress Bar - Always show in Market mode */}
          {viewMode === 'market' && totalTarget > 0 && (
            <div className="mb-1">
              <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all duration-500 ${
                    isComplete ? 'bg-emerald-400' : rateLimited ? 'bg-orange-400' : 'bg-amber-400'
                  }`}
                  style={{ width: `${isComplete ? 100 : loadingProgress}%` }}
                ></div>
              </div>
              <p className={`text-[8px] mt-0.5 text-center ${
                isComplete ? 'text-emerald-400/80' : rateLimited ? 'text-orange-400/80' : 'text-amber-400/80'
              }`}>
                {isComplete 
                  ? `✓ ${totalAnalyzed} stocks scanned` 
                  : rateLimited 
                    ? `Paused ${rateLimitSecondsRemaining}s • ${totalAnalyzed}/${totalTarget} (${loadingProgress}%)`
                    : `Scanning ${totalAnalyzed}/${totalTarget} • ${estimateTimeRemaining() || ''}`
                }
              </p>
            </div>
          )}
          
          {/* Toggle Buttons */}
          <div className="flex bg-slate-700/50 rounded p-0.5">
            <button
              onClick={() => setViewMode('market')}
              className={`flex-1 text-[9px] font-medium py-0.5 px-2 rounded transition-all ${
                viewMode === 'market'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              Market
            </button>
            <button
              onClick={() => setViewMode('portfolio')}
              className={`flex-1 text-[9px] font-medium py-0.5 px-2 rounded transition-all ${
                viewMode === 'portfolio'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              My Stocks
            </button>
          </div>
        </div>
        
        <div className="flex-1 overflow-hidden p-1 flex flex-col gap-0.5">
          {/* Gainers */}
          <div className="flex-1 flex flex-col">
            <p className="text-[10px] text-emerald-400 font-semibold px-1 py-0.5 bg-slate-800/95">
              🔺 GAINERS {gainers.length > 0 ? `(${gainers.length})` : ''}
            </p>
            <div className="flex-1 flex flex-col justify-evenly">
              {gainers.length > 0 ? (
                gainers.map((stock, i) => (
                  <div 
                    key={i} 
                    className="flex justify-between items-center px-1.5 py-[3px] bg-emerald-500/10 rounded hover:bg-emerald-500/30 transition-colors cursor-pointer"
                    onMouseEnter={(e) => handleMouseEnter(stock, e)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <span className="text-[11px] font-medium text-slate-200">{stock.symbol}</span>
                    <span className="text-[11px] font-bold text-emerald-400">+{stock.changePercent?.toFixed(2)}%</span>
                  </div>
                ))
              ) : (
                <div className="px-2 py-2 text-center">
                  <div className="animate-pulse flex flex-col gap-1">
                    <div className="h-3 bg-slate-700/50 rounded"></div>
                    <div className="h-3 bg-slate-700/50 rounded"></div>
                  </div>
                  <p className="text-[9px] text-slate-500 mt-1">Loading...</p>
                </div>
              )}
            </div>
          </div>
          {/* Losers */}
          <div className="flex-1 flex flex-col">
            <p className="text-[10px] text-red-400 font-semibold px-1 py-0.5 bg-slate-800/95">
              🔻 LOSERS {losers.length > 0 ? `(${losers.length})` : ''}
            </p>
            <div className="flex-1 flex flex-col justify-evenly">
              {losers.length > 0 ? (
                losers.map((stock, i) => (
                  <div 
                    key={i} 
                    className="flex justify-between items-center px-1.5 py-[3px] bg-red-500/10 rounded hover:bg-red-500/30 transition-colors cursor-pointer"
                    onMouseEnter={(e) => handleMouseEnter(stock, e)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <span className="text-[11px] font-medium text-slate-200">{stock.symbol}</span>
                    <span className="text-[11px] font-bold text-red-400">{stock.changePercent?.toFixed(2)}%</span>
                  </div>
                ))
              ) : (
                <div className="px-2 py-2 text-center">
                  <div className="animate-pulse flex flex-col gap-1">
                    <div className="h-3 bg-slate-700/50 rounded"></div>
                    <div className="h-3 bg-slate-700/50 rounded"></div>
                  </div>
                  <p className="text-[9px] text-slate-500 mt-1">Loading...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tooltip */}
      {hoveredStock && (
        <div 
          className="fixed z-[100] bg-slate-900 border border-slate-600 rounded-lg shadow-xl p-3 min-w-[220px]"
          style={{
            left: tooltipPosition.x,
            top: tooltipPosition.y,
            transform: 'translateY(-20%)'
          }}
        >
          {/* Header */}
          <div className="border-b border-slate-700 pb-2 mb-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-white">{hoveredStock.symbol}</span>
              <span className={`text-sm font-bold ${hoveredStock.changePercent >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {hoveredStock.changePercent >= 0 ? '+' : ''}{hoveredStock.changePercent?.toFixed(2)}%
              </span>
            </div>
            <p className="text-[10px] text-slate-400 mt-0.5 truncate">{hoveredStock.name}</p>
          </div>
          
          {/* Price Info */}
          <div className="space-y-1.5">
            <div className="flex justify-between items-center">
              <span className="text-[10px] text-slate-400">Current Price</span>
              <span className="text-xs font-semibold text-white">${formatNumber(hoveredStock.price)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[10px] text-slate-400">Change</span>
              <span className={`text-xs font-semibold ${hoveredStock.change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {hoveredStock.change >= 0 ? '+' : ''}${formatNumber(hoveredStock.change)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[10px] text-slate-400">Day Range</span>
              <span className="text-xs text-slate-300">
                ${formatNumber(hoveredStock.dayLow)} - ${formatNumber(hoveredStock.dayHigh)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[10px] text-slate-400">52-Week High</span>
              <span className="text-xs text-slate-300">${formatNumber(hoveredStock.week52High)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[10px] text-slate-400">Open</span>
              <span className="text-xs text-slate-300">${formatNumber(hoveredStock.openPrice)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[10px] text-slate-400">Prev. Close</span>
              <span className="text-xs text-slate-300">${formatNumber(hoveredStock.previousClose)}</span>
            </div>
          </div>
          
          {/* Arrow pointer */}
          <div className="absolute left-0 top-1/2 transform -translate-x-full -translate-y-1/2">
            <div className="w-0 h-0 border-t-8 border-b-8 border-r-8 border-transparent border-r-slate-600"></div>
          </div>
        </div>
      )}
    </>
  );
};

export default MarketMoversPanel;
